<script lang="ts">
import AppTrustpilot from '@/components/common/AppTrustpilot.vue'
import { defineComponent } from 'vue'

import BaseCard from './BaseCard.vue'

export default defineComponent({
  components: {
    BaseCard,
    AppTrustpilot,
  },
  props: {
    emptySlots: {
      type: Number,
      default: 1,
    },
  },
  methods: {
    async redirectToDailySpins() {
      await this.$router.push({ name: 'daily-spins' })
    },
  },
})
</script>

<template>
  <BaseCard
    class="size-full gap-0 p-0 coming-soon-card"
    :style="{
      gridColumn: `span ${emptySlots}`,
    }"
  >
    <div
      class="text-md flex size-full bg-indigo-950 bg-[url('@/assets/images/banners/texture-black.webp')] bg-right-bottom bg-no-repeat text-white mask-squiggle"
      :class="{
        'py-3 flex-col justify-center text-center overflow-hidden relative':
          emptySlots === 1,
        'pl-4 @md/main:pl-6 flex-col @md/main:flex-row items-center text-center @md/main:text-left relative overflow-hidden':
          emptySlots > 1,
      }"
      :style="{
        '--mask-squiggle-url': `url('/assets/images/masks/squiggle-lg.svg')`,
      }"
    >
      <!-- Content section (text, button, trustpilot) -->
      <div
        class="flex flex-col relative z-10"
        :class="{
          'h-full justify-center': emptySlots === 1,
          'flex-1 pr-4 @md/main:pr-60 py-6 justify-between gap-2 h-full':
            emptySlots > 1,
        }"
      >
        <div
          :class="{
            'text-center': emptySlots === 1,
            'text-center @md/main:text-left ml-0 @md/main:ml-2': emptySlots > 1,
          }"
        >
          <h2
            class="!font-black leading-none"
            :class="{
              'text-2xl @md/main:text-3xl': emptySlots <= 2,
              'text-3xl @md/main:text-4xl': emptySlots > 2,
            }"
          >
            <template v-if="emptySlots === 1"> Want Free<br /> </template>
            <template v-else> Want Free </template>
            Credit?
          </h2>
          <p
            v-if="emptySlots > 1"
            class="text-white pt-4"
            :class="{
              'text-base @md/main:text-lg': emptySlots === 2,
              'text-lg @md/main:text-xl': emptySlots > 2,
            }"
          >
            Try our daily game now!
          </p>
        </div>
        <div
          v-if="emptySlots === 1"
          class="flex justify-center items-center overflow-x-visible overflow-y-hidden w-[130%] -mx-[15%]"
        >
          <img
            src="@/assets/images/banners/icons.png"
            alt="Gaming Icons"
            class="w-[100%] object-cover"
          />
        </div>

        <!-- Button and Trustpilot - stacked for emptySlots === 1, side by side for emptySlots > 1 -->
        <template v-if="emptySlots === 1">
          <div class="mb-2 flex justify-center">
            <AppRegisterSignInModal id="daily-game-signup-signin-modal">
              <template v-if="$userStore.isLoggedOut" #button="{ openModal }">
                <AppButton
                  class="font-bold py-4 px-8 rounded-lg transition-colors button-primary min-w-fit whitespace-nowrap"
                  title="Play For Free"
                  @click="openModal('register', redirectToDailySpins)"
                >
                  Play For Free
                </AppButton>
              </template>
            </AppRegisterSignInModal>

            <AppButton
              v-if="$userStore.isLoggedIn"
              class="font-bold py-4 px-8 rounded-lg transition-colors button-primary min-w-fit whitespace-nowrap"
              title="Play For Free"
              :route="{ name: 'daily-spins' }"
            >
              Play For Free
            </AppButton>
          </div>
          <div class="text-center">
            <AppTrustpilot />
          </div>
        </template>

        <template v-else>
          <div class="flex flex-col gap-3">
            <!-- Side by side when emptySlots > 2, stacked otherwise -->
            <div
              :class="{
                'flex flex-col @sm/main:flex-row items-center gap-3 flex-wrap justify-center @md/main:justify-start':
                  emptySlots > 2,
                'flex flex-col gap-3': emptySlots <= 2,
              }"
            >
              <AppRegisterSignInModal id="daily-game-signup-signin-modal">
                <template v-if="$userStore.isLoggedOut" #button="{ openModal }">
                  <AppButton
                    class="font-bold py-4 px-8 rounded-lg transition-colors button-primary min-w-fit whitespace-nowrap"
                    title="Play For Free"
                    @click="openModal('register', redirectToDailySpins)"
                  >
                    Play For Free
                  </AppButton>
                </template>
              </AppRegisterSignInModal>

              <AppButton
                v-if="$userStore.isLoggedIn"
                title="Play For Free"
                :route="{ name: 'daily-spins' }"
                class="!font-bold"
                :class="{
                  'py-2 px-4 rounded-lg transition-colors button-primary flex-shrink-0 text-md w-full @sm/main:w-auto':
                    emptySlots === 2,
                  'py-3 px-6 @md/main:px-8 rounded-lg transition-colors button-primary flex-shrink-0 text-md @md/main:text-base w-full @sm/main:w-auto @sm/main:min-w-48 whitespace-nowrap':
                    emptySlots > 2,
                }"
              >
                Play For Free
              </AppButton>
              <div class="flex-shrink-0 pt-1">
                <AppTrustpilot />
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Gaming Icons - Single vertical image for emptySlots > 1 -->
      <div
        v-if="emptySlots > 1"
        class="hidden @md/main:flex absolute right-2 top-0 bottom-0 items-center justify-center"
      >
        <img
          src="@/assets/images/banners/vertical-icons-large.png"
          alt="Gaming Icons"
          class="h-[130%] w-auto object-contain"
        />
      </div>
    </div>
  </BaseCard>
</template>
